import { ArrowRight, Star } from "lucide-react";
import type { AvailableApp } from "@/lib/api/controlPlaneClient";

/**
 * Canva-style featured banner: full-bleed image background with the "Featured"
 * eyebrow, headline, description and a solid CTA laid over the dark left side.
 */
export function FeaturedHero({
  app,
  isInstalled,
  onClick,
}: {
  app: AvailableApp;
  isInstalled?: boolean;
  onClick: (app: AvailableApp) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(app)}
      aria-label={`Featured: ${app.app_name}`}
      className="group relative mb-8 flex min-h-[220px] w-full items-center overflow-hidden rounded-2xl p-7 text-left text-white shadow-apple-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2794F0] focus-visible:ring-offset-2 sm:p-9"
    >
      {/* Image background */}
      <img
        src="/featured-banner-bg.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* Legibility scrim over the copy side */}
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#1a1147]/80 via-[#1a1147]/35 to-transparent"
        aria-hidden="true"
      />

      {/* Copy */}
      <div className="relative min-w-0 max-w-xl">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] backdrop-blur-sm">
          <Star className="h-3 w-3 fill-white text-white" />
          Featured
        </span>
        <h2 className="mt-3 text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl">
          {app.app_name}
        </h2>
        <p className="mt-2.5 text-sm leading-relaxed text-white/85 sm:text-[15px] line-clamp-2">
          {app.description}
        </p>
        <span className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-[#4f2fbf] shadow-sm transition-transform duration-200 group-hover:gap-3">
          {isInstalled ? "Open app" : "Get this app"}
          <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
        </span>
      </div>
    </button>
  );
}
